import React from 'react'
import {
  RecordLink,
  RecordsSelect
} from 'portal/components/RecordsTable/RecordsColumn'
import { icons } from 'portal/lib/primitives'
import { RECORD_STATUS } from 'portal/lib/constants'
import { recordsComponentFactory } from './recordsComponentFactory'
import { Input, Tooltip } from 'antd'

const { Search } = Input

function setCellId(record, rowIndex) {
  if (rowIndex === 0) {
    return {}
  }
  if (record.removed) {
    return {
      id: 'RecordsTableRow',
      style: {
        textDecoration: 'line-through',
        opacity: '0.5'
      }
    }
  }
  return {
    id: 'RecordsTableCell'
  }
}

export const Delivery = recordsComponentFactory({
  ModuleName: 'Delivery',
  dateField: 'deliveryDate',
  buildRecordsForDisplay: function (records, projectTimeZone) {
    return {
      data: (records ?? []).map(record => {
        const productGroupNames = record.data.products
          ?.reduce((pg, item) => {
            if (!item.isDeleted && !item.isRemoved && item.productGroupName) {
              pg.push(item.productGroupName)
            }

            // Remove duplicates and return
            return [...new Set([...pg])]
          }, [])
          .join(',\n')

        return {
          record: record,
          createdAt: new Date(record.createdAt).toLocaleDateString([], {
            timeZone: projectTimeZone,
            year: 'numeric',
            month: '2-digit',
            day: '2-digit'
          }),
          date: record.data.deliveryArrivalDate
            ? new Date(record.data.deliveryArrivalDate).toLocaleString([], {
                timeZone: projectTimeZone,
                year: 'numeric',
                month: '2-digit',
                day: '2-digit'
              })
            : undefined,
          deliveryId: record.data.deliveryId,
          deliveryAddressKnown: record.data.deliveryAddressKnown,
          tradeContractor: record.data.tradeContractor,
          knownSupplierName: record.data.knownSupplierName,
          recordIdRaw: record.recordId,
          productGroupNames: productGroupNames,
          productsWithDesignMatchesCount: record.productsWithDesignMatchesCount,
          removed: record.status === RECORD_STATUS.REMOVED
        }
      })
    }
  },
  buildColumnsForTable: function (
    { size },
    state,
    fetchOptions,
    assignedToOptions,
    filters,
    updateFilters,
    projectId,
    tableId,
    accountId,
    sortById
  ) {
    return [
      {
        dataIndex: 'createdAt',
        className: 'createdAt',
        title: 'Created At',
        width: '8.125rem',
        onCell: setCellId
      },
      {
        dataIndex: 'date',
        className: 'arrivalDate',
        title: 'Arrival Date',
        width: '8.125rem',
        onCell: setCellId
      },
      {
        dataIndex: 'deliveryId',
        className: 'deliveryId',
        sorter: true,
        ellipsis: true,
        render: (_, record, index) => {
          if (index === 0) {
            return (
              <Search
                defaultValue={filters.deliveryId}
                placeholder={'Note: Case sensitive'}
                onSearch={value => {
                  updateFilters('deliveryId', value)
                }}
                onBlur={e => {
                  if (e.target.value !== filters.deliveryId) {
                    updateFilters('deliveryId', e.target.value)
                  }
                }}
                data-testid={'deliveryIdFilter'}
              />
            )
          } else {
            return record.deliveryId
          }
        },
        title: 'Delivery ID',
        onCell: setCellId
      },
      size > 1 && {
        dataIndex: 'deliveryAddressKnown',
        className: 'deliveryAddressKnown',
        ellipsis: true,
        render: (_, record, index) => {
          if (index === 0) {
            return RecordsSelect(
              'deliveryAddressKnown',
              state,
              fetchOptions,
              filters,
              updateFilters,
              projectId,
              tableId
            )
          } else {
            return record.deliveryAddressKnown
          }
        },
        title: 'Delivery Address',
        onCell: setCellId
      },
      size > 1 && {
        title: 'Trade Contractor',
        ellipsis: true,
        render: (_, record, index) => {
          if (index === 0) {
            return RecordsSelect(
              'tradeContractor',
              state,
              fetchOptions,
              filters,
              updateFilters,
              projectId,
              tableId
            )
          } else {
            return record.tradeContractor
          }
        },
        dataIndex: 'tradeContractor',
        className: 'tradeContractor',
        onCell: setCellId
      },
      size > 2 && {
        title: 'Supplier Company',
        ellipsis: true,
        render: (_, record, index) => {
          if (index === 0) {
            return RecordsSelect(
              'knownSupplierName',
              state,
              fetchOptions,
              filters,
              updateFilters,
              projectId,
              tableId
            )
          } else {
            return record.knownSupplierName
          }
        },
        dataIndex: 'knownSupplierName',
        className: 'knownSupplierName',
        onCell: setCellId
      },
      size > 1 && {
        title: 'Product Groups',
        ellipsis: true,
        render: (_, record, index) => {
          if (index === 0) {
            return RecordsSelect(
              'products[].productGroupName',
              state,
              fetchOptions,
              filters,
              updateFilters,
              projectId,
              tableId
            )
          } else {
            return record.productGroupNames
          }
        },
        dataIndex: 'productGroupNames',
        className: 'productGroupNames',
        onCell: setCellId
      },
      size > 1 && {
        title: 'Assigned To',
        ellipsis: false,
        render: (_, record, index) => {
          if (index === 0) {
            return RecordsSelect(
              'assignedTo',
              state,
              assignedToOptions,
              filters,
              updateFilters,
              projectId,
              tableId
            )
          } else {
            return record.assignedTo
          }
        },
        dataIndex: 'assignedTo',
        className: 'assignedTo',
        width: '8rem',
        align: 'center'
      },
      {
        dataIndex: 'productsWithDesignMatchesCount',
        className: 'productsWithDesignMatchesCount',
        title: (
          <Tooltip title="Number of products with design matches for this record">
            <icons.DesignMatch alt="Design Matches" />
          </Tooltip>
        ),
        width: '3rem',
        align: 'center',
        render: (num, record, index) => {
          if (index === 0 || num === 0) {
            return null
          }
          const className = record.hasMaterialWatchlistAlert
            ? ' record-alert-cell'
            : ''
          return (
            <Tooltip
              title={className ? 'Record has material watchlist alert' : ''}
            >
              <div
                className={`design-matches-count${className}`}
                data-testid="designMatchesCountCell"
              >
                {num}
              </div>
            </Tooltip>
          )
        }
      },
      RecordLink(setCellId, filters, updateFilters)
    ]
  },
  initialFilterState: {
    'deliveryId': '',
    'deliveryDate': { startDate: undefined, endDate: undefined },
    'deliveryAddressKnown': [],
    'tradeContractor': [],
    'knownSupplierName': [],
    'startDate': undefined,
    'endDate': undefined,
    'products[].productGroupName': []
  }
})
